<template>
    <div class="pagina">
        <!-- Header -->
        <header class="header">
            <img src="/img/logo-preta.png" height="36" alt="Bike Registrada" />
        </header>

        <!-- Steps -->
        <RegistroBikeStepper :current-step="0" :steps="['Dados pessoais', 'Tipos de registro', 'Finalização']" />

        <!-- Conteúdo -->
        <div class="conteudo">
            <div class="card-form">
                <h2 class="titulo">Bem-vindo(a) a criação de Registro da <strong>Bike Registrada</strong></h2>

                <!-- Nome -->
                <div class="campo">
                    <label class="campo-label">Como podemos chamar você?</label>
                    <input v-model="nome" type="text" class="campo-input" placeholder="Informe o seu nome"
                        autocomplete="off" />
                </div>

                <!-- CPF -->
                <div class="campo">
                    <label class="campo-label">Qual o seu CPF?</label>
                    <p class="campo-dica">Não se preocupe: seu CPF será usado apenas para verificação de identidade e
                        segurança do
                        registro.</p>
                    <input v-model="cpf" type="text" inputmode="numeric" class="campo-input" :class="{ 'campo-input--erro': cpfInvalido }"
                        placeholder="Informe o CPF" maxlength="14" autocomplete="off" @input="mascararCPF" />
                    <p v-if="cpfInvalido" class="campo-erro">CPF inválido. Verifique e tente novamente.</p>
                </div>

                <!-- Email + Telefone -->
                <div class="campos-duplos">
                    <div class="campo">
                        <label class="campo-label">Qual o seu melhor e-mail?</label>
                        <p class="campo-dica">Não enviamos spam e seus dados são usados apenas para identificação.</p>
                        <input v-model="email" type="email" class="campo-input"
                            :class="{ 'campo-input--erro': emailInvalido }" placeholder="Informe o e-mail"
                            autocomplete="email" @blur="aoSairEmail" @input="aoDigitarEmail" />
                        <p v-if="emailInvalido" class="campo-erro">E-mail inválido. Verifique e tente novamente.</p>
                    </div>
                    <div class="campo">
                        <label class="campo-label">E o seu telefone?</label>
                        <p class="campo-dica">Vamos verificar se existem promoções ativas em sua região.</p>
                        <input v-model="telefone" type="tel" class="campo-input"
                            placeholder="Insira o seu telefone com o DDD" autocomplete="tel" maxlength="15"
                            @input="mascararTelefone" />
                    </div>
                </div>

                <!-- Data de nascimento + UF -->
                <div class="campos-duplos campos-data-uf">
                    <div class="campo">
                        <label class="campo-label">Data de nascimento</label>
                        <input v-model="dataNascimento" type="text" inputmode="numeric" class="campo-input"
                            :class="{ 'campo-input--erro': dataNascimentoInvalida }"
                            placeholder="00/00/0000" maxlength="10" autocomplete="bday"
                            @input="mascararData" @blur="validarData" />
                        <p v-if="dataNascimentoInvalida" class="campo-erro">{{ dataNascimentoErro }}</p>
                    </div>
                    <div class="campo">
                        <label class="campo-label">UF</label>
                        <div class="campo-select-custom" :class="{ 'campo-select-custom--aberto': dropdownUfAberto, 'campo-input--erro': false }" @click="dropdownUfAberto = !dropdownUfAberto" v-click-outside="fecharDropdownUf">
                            <span :class="{ placeholder: !uf }">{{ uf || 'Estado' }}</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :style="{ transform: dropdownUfAberto ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }"><polyline points="6 9 12 15 18 9"/></svg>
                            <ul v-if="dropdownUfAberto" class="campo-select-lista">
                                <li v-for="estado in estados" :key="estado.sigla" :class="{ selecionado: uf === estado.sigla }" @click.stop="selecionarUf(estado.sigla)">
                                    {{ estado.nome }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <p v-if="erroApi" class="campo-erro" style="text-align:center; margin-bottom:12px">{{ erroApi }}</p>

                <div class="rodape-form">
                    <button class="btn-continuar" :disabled="!podeContinuar || carregando" @click="continuar">
                        <span v-if="carregando">Aguarde...</span>
                        <template v-else>
                            Continuar
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                                xmlns="http://www.w3.org/2000/svg" style="transform:scaleX(-1)">
                                <path
                                    d="M12.5017 6.66829H0.835041M0.835041 6.66829L6.66837 0.834961M0.835041 6.66829L6.66837 12.5016"
                                    stroke="currentColor" stroke-width="1.67" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                        </template>
                    </button>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <footer class="footer">
            <span>Copyright © 2025 Bike Registrada Online LTDA.</span>
            <div class="footer-contatos">
                <span>Contatos:</span>
                <a href="https://wa.me/556135224521" target="_blank" rel="noopener">
                    <i class="fab fa-whatsapp"></i> (61) 3522-4521
                </a>
                <a href="mailto:seguro@bikeregistrada.com.br">
                    <i class="far fa-envelope"></i> seguro@bikeregistrada.com.br
                </a>
            </div>
        </footer>
    </div>
</template>

<script>
import axios from "axios";
import config from "@/config";
import auth from "@/auth";
import RegistroBikeStepper from "@/components/RegistroBikeStepper.vue";
import validacaoDataNascimento from "@/helpers/validacaoDataNascimento";

function validarCPF(cpf) {
    const nums = cpf.replace(/\D/g, "");
    if (nums.length !== 11 || /^(\d)\1+$/.test(nums)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(nums[i]) * (10 - i);
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(nums[9])) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(nums[i]) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    return resto === parseInt(nums[10]);
}

const clickOutside = {
    bind(el, binding) {
        el._clickOutsideHandler = (e) => {
            if (!el.contains(e.target)) binding.value();
        };
        document.addEventListener("click", el._clickOutsideHandler);
    },
    unbind(el) {
        document.removeEventListener("click", el._clickOutsideHandler);
    },
};

export default {
    name: "DadosUnificados",
    components: { RegistroBikeStepper },
    directives: { clickOutside },
    data() {
        return {
            nome: "",
            cpf: "",
            cpfInvalido: false,
            email: "",
            emailInvalido: false,
            emailTocado: false,
            telefone: "",
            dataNascimento: "",
            dataNascimentoInvalida: false,
            dataNascimentoErro: "",
            uf: "",
            carregando: false,
            erroApi: "",
            dropdownUfAberto: false,
            estados: [
                { sigla: "AC", nome: "Acre" },
                { sigla: "AL", nome: "Alagoas" },
                { sigla: "AP", nome: "Amapá" },
                { sigla: "AM", nome: "Amazonas" },
                { sigla: "BA", nome: "Bahia" },
                { sigla: "CE", nome: "Ceará" },
                { sigla: "DF", nome: "Distrito Federal" },
                { sigla: "ES", nome: "Espírito Santo" },
                { sigla: "GO", nome: "Goiás" },
                { sigla: "MA", nome: "Maranhão" },
                { sigla: "MT", nome: "Mato Grosso" },
                { sigla: "MS", nome: "Mato Grosso do Sul" },
                { sigla: "MG", nome: "Minas Gerais" },
                { sigla: "PA", nome: "Pará" },
                { sigla: "PB", nome: "Paraíba" },
                { sigla: "PR", nome: "Paraná" },
                { sigla: "PE", nome: "Pernambuco" },
                { sigla: "PI", nome: "Piauí" },
                { sigla: "RJ", nome: "Rio de Janeiro" },
                { sigla: "RN", nome: "Rio Grande do Norte" },
                { sigla: "RS", nome: "Rio Grande do Sul" },
                { sigla: "RO", nome: "Rondônia" },
                { sigla: "RR", nome: "Roraima" },
                { sigla: "SC", nome: "Santa Catarina" },
                { sigla: "SP", nome: "São Paulo" },
                { sigla: "SE", nome: "Sergipe" },
                { sigla: "TO", nome: "Tocantins" },
            ],
        };
    },
    computed: {
        podeContinuar() {
            return (
                this.nome.trim().length > 0 &&
                this.cpf.replace(/\D/g, "").length === 11 &&
                !this.cpfInvalido &&
                this.email.trim().length > 0 &&
                !this.emailInvalido &&
                this.telefone.replace(/\D/g, "").length >= 10 &&
                this.dataNascimento.length === 10 &&
                !this.dataNascimentoInvalida &&
                this.uf !== ""
            );
        },
    },
    created() {
        this.nome = sessionStorage.getItem("registro_nome") || "";
        this.email = sessionStorage.getItem("registro_email") || "";
        const tel = sessionStorage.getItem("registro_telefone") || "";
        if (tel) {
            this.telefone = tel.replace(/\D/g, "").replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
        }
        const cpfSalvo = sessionStorage.getItem("registro_cpf") || "";
        if (cpfSalvo) {
            this.cpf = cpfSalvo;
            this.mascararCPF();
        }
        this.dataNascimento = sessionStorage.getItem("registro_data_nascimento") || "";
        this.uf = sessionStorage.getItem("registro_uf") || "";
    },
    methods: {
        mascararCPF() {
            let v = this.cpf.replace(/\D/g, "");
            if (v.length > 11) v = v.slice(0, 11);
            if (v.length > 9) {
                v = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
            } else if (v.length > 6) {
                v = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
            } else if (v.length > 3) {
                v = `${v.slice(0, 3)}.${v.slice(3)}`;
            }
            this.cpf = v;
            if (v.replace(/\D/g, "").length === 11) {
                this.cpfInvalido = !validarCPF(v);
            } else {
                this.cpfInvalido = false;
            }
        },
        mascararData() {
            let v = this.dataNascimento.replace(/\D/g, "");
            if (v.length > 8) v = v.slice(0, 8);
            if (v.length > 4) {
                v = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`;
            } else if (v.length > 2) {
                v = `${v.slice(0, 2)}/${v.slice(2)}`;
            }
            this.dataNascimento = v;
            this.dataNascimentoInvalida = false;
            this.dataNascimentoErro = "";
        },
        validarData() {
            if (this.dataNascimento.length < 10) return;
            const resultado = validacaoDataNascimento(this.dataNascimento, true);
            if (resultado !== true) {
                this.dataNascimentoInvalida = true;
                this.dataNascimentoErro = resultado;
            } else {
                this.dataNascimentoInvalida = false;
                this.dataNascimentoErro = "";
            }
        },
        mascararTelefone() {
            let v = this.telefone.replace(/\D/g, "");
            if (v.length > 11) v = v.slice(0, 11);
            if (v.length > 7) {
                v = `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
            } else if (v.length > 2) {
                v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
            } else if (v.length > 0) {
                v = `(${v}`;
            }
            this.telefone = v;
        },
        validarEmail() {
            if (!this.email.trim()) {
                this.emailInvalido = false;
                return;
            }
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            this.emailInvalido = !re.test(this.email.trim());
        },
        aoSairEmail() {
            this.emailTocado = true;
            this.validarEmail();
        },
        aoDigitarEmail() {
            if (this.emailTocado) this.validarEmail();
        },
        fecharDropdownUf() {
            this.dropdownUfAberto = false;
        },
        selecionarUf(sigla) {
            this.uf = sigla;
            this.dropdownUfAberto = false;
        },
        voltar() {
            this.$router.back();
        },
        async continuar() {
            if (!this.podeContinuar || this.carregando) return;
            this.erroApi = "";
            this.carregando = true;
            const cpfDigits = this.cpf.replace(/\D/g, "");
            sessionStorage.setItem("registro_nome", this.nome.trim());
            sessionStorage.setItem("registro_email", this.email.trim());
            sessionStorage.setItem("registro_telefone", this.telefone.replace(/\D/g, ""));
            sessionStorage.setItem("registro_cpf", cpfDigits);
            sessionStorage.setItem("registro_data_nascimento", this.dataNascimento);
            sessionStorage.setItem("registro_uf", this.uf);
            try {
                const response = await axios.post(
                    `${config.API_URLV2}/cliente/criar-ou-atualizar`,
                    {
                        nome: this.nome.trim(),
                        email: this.email.trim(),
                        cpf: cpfDigits,
                        telefone: this.telefone.replace(/\D/g, ""),
                        nascimento: new Date(this.dataNascimento.split('/').reverse().join('-')).toISOString(),
                        uf: this.uf,
                        origem: this.$route.name,
                    },
                    { headers: { Authorization: undefined } }
                );
                auth.setUserInfo(response.data);
                sessionStorage.setItem("registro_id_cliente", response.data.id);
                this.$router.push({ name: "registro-bike-planos" });
            } catch (e) {
                const erros = e.response && e.response.data && e.response.data.erros;
                this.erroApi = erros && erros.length > 0 ? erros[0] : "Ocorreu um erro ao salvar seus dados. Tente novamente.";
            } finally {
                this.carregando = false;
            }
        },
    },
};
</script>

<style scoped>
.pagina {
    min-height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background-color: #f9f9f9;
    background-image: url(/img/bike-mundo.svg);
    background-position: right bottom;
    background-repeat: no-repeat;
    background-size: 35%;
    font-family: Inter !important;
    display: flex;
    flex-direction: column;
    position: relative;
}

/* Header */
.header {
    padding: 16px 32px;
    padding-top: calc(16px + env(safe-area-inset-top));
    background: #f9f9f9;
    display: flex;
    align-items: center;
    position: relative;
    z-index: 10;
}

/* Conteúdo */
.conteudo {
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 16px;
    position: relative;
    z-index: 20;
}

.card-form {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
    padding: 40px 48px;
    width: 100%;
    max-width: 652px;
}

.titulo {
    font-size: 22px;
    font-weight: 700;
    color: #4FA935;
    line-height: 1.35;
    margin-bottom: 28px;
}

.titulo strong {
    font-weight: 700;
}

.campo {
    margin-bottom: 20px;
}

.campos-duplos {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;
}

.campos-duplos .campo {
    margin-bottom: 0;
}

.campo-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 4px;
}

.campo-dica {
    font-size: 12px;
    color: #94A3B8;
    margin-bottom: 8px;
    line-height: 1.4;
}

.campo-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    font-size: 14px;
    color: #333;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
    background: #fff;
}

.campo-input:focus {
    border-color: #4FA935;
}

.campo-input::placeholder {
    color: #CBD5E1;
}

.campo-input--erro {
    border-color: #e53935;
}

.campo-select-custom {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    font-size: 14px;
    color: #333;
    background: #fff;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    z-index: 10;
    user-select: none;
    transition: border-color 0.2s;
}

.campo-select-custom:focus-within,
.campo-select-custom--aberto {
    border-color: #4FA935;
}

.campo-select-custom .placeholder {
    color: #CBD5E1;
}

.campo-select-lista {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    background: #fff;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    max-height: 220px;
    overflow-y: auto;
    z-index: 100;
    list-style: none;
    margin: 0;
    padding: 4px 0;
}

.campo-select-lista li {
    padding: 10px 16px;
    font-size: 14px;
    color: #333;
    cursor: pointer;
    white-space: nowrap;
}

.campo-select-lista li:hover {
    background: #f0faea;
}

.campo-select-lista li.selecionado {
    background: #e8f5e2;
    color: #4b9c2e;
    font-weight: 600;
}

.campo-erro {
    font-size: 12px;
    color: #e53935;
    margin-top: 6px;
    margin-bottom: 0;
}

/* Rodapé */
.rodape-form {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 48px;
}

.btn-voltar {
    border: none;
    display: flex;
    padding: 12px 24px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    background: #F0F3F8;
    color: #425994;
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
}

.btn-voltar:hover:not(:disabled) {
    background: #f0f0f0;
}

.btn-voltar:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-continuar {
    background: #4b9c2e;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 12px 28px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: background 0.2s;
}

.btn-continuar:hover:not(:disabled) {
    background: #3d8224;
}

.btn-continuar:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Footer */
.footer {
    padding: 14px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    color: #64748B;
    position: relative;
    z-index: 10;
}

.footer-contatos {
    display: flex;
    align-items: center;
    gap: 20px;
}

.footer-contatos a {
    color: #4FA935;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 5px;
}

.footer-contatos a:hover {
    color: #4fa935;
}

@media (max-width: 600px) {
    .card-form {
        padding: 28px 20px;
    }

    .pagina {
        background-size: 75%;
    }

    .campos-duplos {
        grid-template-columns: 1fr;
    }

    .campos-data-uf {
        grid-template-columns: 1fr 1fr;
    }

    .rodape-form {
        gap: 12px;
        justify-content: flex-end;
    }

    .btn-continuar {
        justify-content: center;
    }

    .footer {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
        padding: 14px 20px;
        font-size: 10px;
        color: #64748B;
    }

    .header {
        background: #fff;
        border-bottom: 1px solid #E2E8F0;
    }
}
</style>
